// 六角能力「依據」說明 — 點 ⓘ 展開，告訴使用者每項能力依據哪個參數（不揭露公式）。
// ⚠️ 一律定義在模組層級，勿 inline 進其他元件內（見 memory：inline 子元件會無限重掛卡死）。
import React, { useState, useContext } from 'react';
import { TrendingUp, Shield, Scale, Zap, Target, RefreshCw } from 'lucide-react';
import { LanguageContext } from 'context/LanguageContext';
import { getLangPack } from '../../language/langUtil';

const ORANGE = '#F36D26';

// key 對應 RADAR_AXES 六軸；圖示用 lucide（跟站上一致）
const ICONS = { return: TrendingUp, defense: Shield, stability: Scale, burst: Zap, winRate: Target, adaptability: RefreshCw };

const TW = {
  title: '各能力的依據',
  hint: '依策略的回測數據換算，數值僅供參考',
  basedOn: '依據',
  items: [
    { key: 'return',       name: '報酬力', param: '年化報酬率',   desc: '長期能賺多少' },
    { key: 'defense',      name: '防禦力', param: '最大回撤',     desc: '最糟時扛不扛得住' },
    { key: 'stability',    name: '穩定度', param: '獲利因子',     desc: '賺賠比例扎不扎實' },
    { key: 'burst',        name: '爆發力', param: '單月最佳報酬', desc: '抓到行情能衝多高' },
    { key: 'winRate',      name: '命中率', param: '歷史勝率',     desc: '進場獲利的比例' },
    { key: 'adaptability', name: '適應性', param: '獲利月份占比', desc: '多少月份都能獲利' },
  ],
};
const CN = {
  title: '各能力的依据',
  hint: '依策略的回测数据换算，数值仅供参考',
  basedOn: '依据',
  items: [
    { key: 'return',       name: '报酬力', param: '年化报酬率',   desc: '长期能赚多少' },
    { key: 'defense',      name: '防御力', param: '最大回撤',     desc: '最糟时扛不扛得住' },
    { key: 'stability',    name: '稳定度', param: '获利因子',     desc: '赚赔比例扎不扎实' },
    { key: 'burst',        name: '爆发力', param: '单月最佳报酬', desc: '抓到行情能冲多高' },
    { key: 'winRate',      name: '命中率', param: '历史胜率',     desc: '进场获利的比例' },
    { key: 'adaptability', name: '适应性', param: '获利月份占比', desc: '多少月份都能获利' },
  ],
};
const EN_US = {
  title: 'What each score is based on',
  hint: 'Derived from the strategy’s backtest data — for reference only',
  basedOn: 'based on',
  items: [
    { key: 'return',       name: 'Return',       param: 'annualized return',   desc: 'how much it earns long-term' },
    { key: 'defense',      name: 'Defense',      param: 'max drawdown',        desc: 'how well it holds up at worst' },
    { key: 'stability',    name: 'Stability',    param: 'profit factor',       desc: 'how solid the win/loss ratio is' },
    { key: 'burst',        name: 'Burst',        param: 'best single month',   desc: 'upside when it catches a move' },
    { key: 'winRate',      name: 'Hit rate',     param: 'historical win rate', desc: 'share of winning trades' },
    { key: 'adaptability', name: 'Adaptability', param: 'profitable months',   desc: 'how often it stays profitable' },
  ],
};

// 供 per-axis tooltip 等重用：依語系取得能力依據資料（{ title, hint, basedOn, items[] }）
export function getAbilityBasis(language) {
  return getLangPack(language, { TW, CN, EN_US, EN_GB: EN_US });
}

export default function AbilityBasisInfo() {
  const { language } = useContext(LanguageContext);
  const L = getAbilityBasis(language);
  const [open, setOpen] = useState(false);

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={L.title}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 20, height: 20, borderRadius: '50%', border: '1.5px solid #FFF', padding: 0, cursor: 'pointer',
          background: ORANGE, color: '#FFF', boxShadow: '0 1px 5px rgba(243,109,38,0.5)', transition: 'all .15s',
        }}
      >
        <span style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: 'italic', fontWeight: 700, fontSize: 12, lineHeight: 1 }}>i</span>
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: 300, maxWidth: '100%', boxSizing: 'border-box', background: '#FFF', borderRadius: 14, padding: '18px 20px', boxShadow: '0 16px 48px rgba(0,0,0,0.28)' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: '#000' }}>{L.title}</p>
              <button type="button" onClick={() => setOpen(false)} aria-label="close" style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#8A8A8A', fontSize: 20, lineHeight: 1, padding: 0 }}>×</button>
            </div>
            <p style={{ margin: '0 0 12px', fontSize: 11.5, color: '#B0B0B0' }}>{L.hint}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {L.items.map((it) => {
                const Icon = ICONS[it.key];
                return (
                  <div key={it.key} style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                    {Icon && <Icon size={15} style={{ color: ORANGE, flexShrink: 0, marginTop: 2 }} />}
                    <div style={{ lineHeight: 1.35 }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: '#2C2C2C' }}>{it.name}</span>
                      <span style={{ fontSize: 12, color: '#9A9A9A' }}> — {L.basedOn} </span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: ORANGE }}>{it.param}</span>
                      <div style={{ fontSize: 11.5, color: '#8A8A8A' }}>{it.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </span>
  );
}
